/**
 * Populates the rewards page with a logged-in customer's live Smile.io
 * points balance, spend-your-points affordability, and VIP tier status,
 * replacing the editorial fallback content baked into the page.
 *
 * Relies entirely on the Smile.io app embed (Theme Editor > App embeds)
 * already being enabled - that embed is what loads window.Smile and
 * authenticates the current customer. This file never talks to Smile's
 * API directly and never handles credentials/tokens.
 * SDK reference: https://dev.smile.io/js/introduction
 *
 * Spend-your-points dimming: any element with [data-smile-points-required]
 * (icon-box block setting: "Points required (Smile.io)") gets opacity 0.3
 * when the customer's balance is below that number, opacity 1 otherwise -
 * confirmed against Figma (node 706-26048): a 200-point customer sees the
 * 100pt/200pt tiles at full opacity and the 300pt/400pt tiles dimmed.
 *
 * VIP tier status: as of this file being written, the store's VIP program
 * is inactive in Smile Admin (VIP tab), so Smile.customerVipStatus.get()
 * always resolves with every field null - vipTier, nextVipTier,
 * progressValue, deltaToNextVipTier, etc. The field NAMES below are
 * confirmed from Smile's docs, but the exact SHAPE of vipTier/nextVipTier
 * (a bare tier ID vs a nested {id, name, ...} object) has not been
 * verified against a real non-null response. getTierName() below handles
 * both possibilities defensively, but re-check this against real data
 * once the client activates the VIP program.
 */

const SDK_READY_TIMEOUT_MS = 8000;

function log() {
  if (DEBUG && window.console) console.log('[loyalty-live-data]', ...arguments);
}

/**
 * Smile.js exposes its API directly on window.Smile (Smile.customerPointsWallet.get(),
 * etc.) - there is no separate SDK sub-object. Readiness is signaled via a
 * one-time "smile-js-initialized" event dispatched on document once
 * Smile.initialize() finishes its auth handshake (https://dev.smile.io/js/concepts/lifecycle-events),
 * not by a property appearing on window.Smile - polling for one never resolves.
 */
function waitForSmileSdk() {
  return new Promise((resolve, reject) => {
    if (window.Smile && window.Smile.customerPointsWallet && window.Smile.customerVipStatus) {
      log('Smile already initialized before this script ran');
      resolve(window.Smile);
      return;
    }

    const timeoutId = setTimeout(() => {
      document.removeEventListener('smile-js-initialized', onReady);
      log('Smile not ready, timed out after', SDK_READY_TIMEOUT_MS, 'ms');
      reject(new Error('loyalty-live-data: Smile SDK not ready in time'));
    }, SDK_READY_TIMEOUT_MS);

    function onReady() {
      clearTimeout(timeoutId);
      log('smile-js-initialized event received');
      resolve(window.Smile);
    }

    document.addEventListener('smile-js-initialized', onReady, { once: true });
  });
}

const CUSTOMER_API_RETRY_ATTEMPTS = 6;
const CUSTOMER_API_RETRY_DELAY_MS = 700;
const DEBUG = false;

/**
 * The Smile SDK object appears on window before Smile has finished its own
 * internal customer-auth handshake, so the first customer-scoped call
 * (wallet, VIP status) right after the SDK is "ready" can 401 even for a
 * genuinely logged-in customer. Retry with backoff rather than giving up
 * after one attempt.
 */
function withCustomerApiRetry(fn, attemptsLeft, delayMs) {
  attemptsLeft = attemptsLeft || CUSTOMER_API_RETRY_ATTEMPTS;
  delayMs = delayMs || CUSTOMER_API_RETRY_DELAY_MS;

  return fn()
    .then((result) => {
      log('customer API call succeeded, attempts remaining before this one:', attemptsLeft);
      return result;
    })
    .catch((err) => {
      log('customer API call failed:', err && err.message ? err.message : err, '- attempts remaining:', attemptsLeft - 1);
      if (attemptsLeft <= 1) throw err;
      return new Promise((resolve) => setTimeout(resolve, delayMs)).then(() =>
        withCustomerApiRetry(fn, attemptsLeft - 1, delayMs * 1.5)
      );
    });
}

function applyPointsBalance(sdk, wallet) {
  const targets = document.querySelectorAll('[data-smile-points-balance]');
  if (!targets.length || !wallet || typeof wallet.balance !== 'number') return;

  // sdk.formatPoints() throws unless Smile.initialize() was called with a
  // `preload` argument for points settings - not something this theme
  // controls (that's the Smile app embed's own init call) - so never let a
  // formatting failure here take down the rest of this file.
  let formatted = wallet.balance;
  try {
    if (typeof sdk.formatPoints === 'function') formatted = sdk.formatPoints(wallet.balance);
  } catch (err) {
    log('formatPoints() failed, using raw balance instead:', err && err.message ? err.message : err);
  }

  targets.forEach((el) => {
    el.textContent = formatted;
  });
}

function applySpendPointsTiles(wallet) {
  const tiles = document.querySelectorAll('[data-smile-points-required]');
  if (!tiles.length || !wallet || typeof wallet.balance !== 'number') return;

  tiles.forEach((tile) => {
    const required = Number(tile.dataset.smilePointsRequired);
    if (Number.isNaN(required)) return;
    tile.style.opacity = wallet.balance >= required ? '1' : '0.3';
  });
}

function updatePointsWallet(sdk) {
  const hasBalanceTargets = document.querySelectorAll('[data-smile-points-balance]').length > 0;
  const hasSpendTiles = document.querySelectorAll('[data-smile-points-required]').length > 0;
  log('updatePointsWallet: hasBalanceTargets =', hasBalanceTargets, ', hasSpendTiles =', hasSpendTiles);
  if (!hasBalanceTargets && !hasSpendTiles) return;

  withCustomerApiRetry(() => {
    // Re-read window.Smile fresh on every attempt rather than reusing the
    // reference captured when it first became ready, in case Smile
    // reinitializes it once its own auth handshake finishes.
    const currentSdk = window.Smile || sdk;
    return currentSdk.customerPointsWallet.get();
  })
    .then((wallet) => {
      log('wallet fetch succeeded, balance =', wallet && wallet.balance);
      // Each of these renders an independent part of the page - one
      // throwing must never stop the other from running.
      try {
        applyPointsBalance(sdk, wallet);
      } catch (err) {
        log('applyPointsBalance failed:', err && err.message ? err.message : err);
      }
      try {
        applySpendPointsTiles(wallet);
      } catch (err) {
        log('applySpendPointsTiles failed:', err && err.message ? err.message : err);
      }
      log(
        'tiles after applySpendPointsTiles:',
        Array.from(document.querySelectorAll('[data-smile-points-required]')).map((t) => t.style.opacity)
      );
    })
    .catch((err) => {
      log('wallet fetch gave up:', err && err.message ? err.message : err);
      // Leave the editorial fallback content in place.
    });
}

function getTierName(tierRef, tiers) {
  if (!tierRef) return '';
  if (typeof tierRef === 'object') return tierRef.name || '';

  const match = tiers.find((tier) => String(tier.id) === String(tierRef) || tier.name === tierRef);
  return match ? match.name : '';
}

function getTierId(tierRef) {
  if (!tierRef) return null;
  if (typeof tierRef === 'object') return tierRef.id;
  return tierRef;
}

function updateCurrentTierLabel(currentTierId) {
  const tierCells = document.querySelectorAll('[data-smile-tier-id]');
  if (!tierCells.length || currentTierId === null) return;

  let matched = false;

  tierCells.forEach((cell) => {
    const label = cell.querySelector('[data-smile-current-label]');
    if (!label) return;

    const isMatch = String(cell.dataset.smileTierId) === String(currentTierId);
    label.classList.toggle('vip-benefits__current--hidden', !isMatch);
    if (isMatch) matched = true;
  });

  // If none of the rendered tier columns map to the customer's real tier
  // (e.g. the smile_tier_id settings haven't been filled in yet), leave
  // whichever column was manually marked as current alone rather than
  // hiding every label.
  if (!matched) return;
}

function updateProgressBar(vip, tiers) {
  const bar = document.querySelector('[data-smile-vip-progress]');
  if (!bar) return;

  const currentName = getTierName(vip.vipTier, tiers);
  const nextName = getTierName(vip.nextVipTier, tiers);

  if (currentName) {
    const startLabel = bar.querySelector('[data-smile-vip-start-label]');
    if (startLabel) startLabel.textContent = currentName;
  }

  if (nextName) {
    const endLabel = bar.querySelector('[data-smile-vip-end-label]');
    if (endLabel) endLabel.textContent = nextName;
  }

  if (nextName && typeof vip.deltaToNextVipTier === 'number') {
    const currentLabel = bar.querySelector('[data-smile-vip-current-label]');
    if (currentLabel) {
      currentLabel.textContent = `${vip.deltaToNextVipTier} to ${nextName}!`;
    }
  }

  if (typeof vip.progressValue === 'number') {
    const percent = Math.max(0, Math.min(100, vip.progressValue));
    const fill = bar.querySelector('[data-smile-vip-fill]');
    const remaining = bar.querySelector('[data-smile-vip-remaining]');
    if (fill) fill.style.flexGrow = String(percent);
    if (remaining) remaining.style.flexGrow = String(100 - percent);
  }
}

function updateVipStatus(sdk) {
  const hasTierCells = document.querySelectorAll('[data-smile-tier-id]').length > 0;
  const hasProgressBar = document.querySelector('[data-smile-vip-progress]');
  if (!hasTierCells && !hasProgressBar) return;

  Promise.all([
    withCustomerApiRetry(() => {
      const currentSdk = window.Smile || sdk;
      return currentSdk.customerVipStatus.get();
    }),
    sdk.vipTiers.get(),
  ])
    .then(([vip, tiers]) => {
      // VIP program inactive (or customer not yet tiered) - every field on
      // vip comes back null. Leave all editorial/manual fallback content
      // exactly as rendered.
      if (!vip || !vip.vipTier) return;

      updateCurrentTierLabel(getTierId(vip.vipTier));
      if (hasProgressBar) updateProgressBar(vip, tiers || []);
    })
    .catch(() => {
      // Leave editorial fallback content in place.
    });
}

function init() {
  waitForSmileSdk()
    .then((sdk) => {
      updatePointsWallet(sdk);
      updateVipStatus(sdk);
    })
    .catch(() => {
      // Smile SDK unavailable (app embed disabled, blocked, still loading
      // slowly, etc.) - editorial fallback content stays as-is.
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
